import React, { useState, useEffect, useMemo } from 'react';
import { analyticsAPI } from '../../services/api';
import { formatRelativeDate } from '../../utils/dateFormat';
import './Admin.css';
import './Analytics.css';

interface FeatureUsage {
  search: number;
  patents: number;
  reactions: number;
  targets: number;
  pathways: number;
  bioactivity: number;
}

interface SearchTypes {
  substructure: number;
  exact: number;
  similarity: number;
  text: number;
}

interface GuestSummary {
  user_id: string;
  guest_name: string;
  company: string;
  role: string;
  is_active: boolean;
  total_sessions: number;
  total_actions: number;
  avg_session_minutes: number;
  first_seen: string | null;
  last_seen: string | null;
  path_chosen: string | null;
  engagement: 'high' | 'medium' | 'low' | 'none';
  feature_usage: FeatureUsage;
  search_types: SearchTypes;
  exports: number;
}

interface AggregateData {
  active_guests: number;
  expired_guests: number;
  total_sessions: number;
  total_actions: number;
  avg_session_minutes: number;
  unopened_guests: number;
  feature_usage: Record<string, number>;
  path_distribution: Record<string, number>;
}

interface TimelineEvent {
  id: number;
  action_type: string;
  action_category: string;
  page_area: string | null;
  metadata: Record<string, any>;
  created_at: string;
  session_id: string | null;
}

// Path display config
const PATH_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  medchem: { label: 'I Make Molecules', color: '#63b3ed', icon: 'M' },
  ip: { label: 'Protect My IP', color: '#f6ad55', icon: 'P' },
  full: { label: 'The Full Picture', color: '#a371f7', icon: 'F' },
};

const ENGAGEMENT_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  high: { label: 'HIGH', color: '#68d391', bg: 'rgba(104, 211, 145, 0.15)' },
  medium: { label: 'MEDIUM', color: '#f6ad55', bg: 'rgba(246, 173, 85, 0.15)' },
  low: { label: 'LOW', color: '#fc8181', bg: 'rgba(252, 129, 129, 0.15)' },
  none: { label: 'NOT OPENED', color: '#718096', bg: 'rgba(113, 128, 150, 0.15)' },
};

function getFollowupAdvice(engagement: string, userType?: string): string {
  if (userType === 'trial_member' || userType === 'trial') {
    switch (engagement) {
      case 'high': return 'Strong conversion candidate. Discuss annual membership.';
      case 'medium': return 'Moderate interest. Gentle follow-up about specific features.';
      case 'low': return 'May need guidance. Ask what they were looking for.';
      default: return "Haven't opened. Send a nudge \u2014 trial expires soon.";
    }
  }
  if (userType === 'guest') {
    switch (engagement) {
      case 'high': return 'Engaged viewer. Invite to trial membership.';
      case 'medium': return 'Moderate interest. Gentle follow-up about specific features.';
      case 'low': return 'Casual viewer. No action needed yet.';
      default: return "Haven't opened. Send a nudge \u2014 link expires soon.";
    }
  }
  // member / default
  switch (engagement) {
    case 'high': return 'Power user. Potential champion for their organisation.';
    case 'medium': return 'Moderate interest. Gentle follow-up about specific features.';
    case 'low': return 'Retention risk. Check if they need training or support.';
    default: return "Haven't opened. Send a nudge \u2014 may need assistance.";
  }
}

function getRoleLabel(userType?: string): string {
  if (userType === 'trial_member' || userType === 'trial') return 'Trial Member';
  if (userType === 'guest') return 'Guest';
  return 'Member';
}

const AnalyticsPage: React.FC = () => {
  const [guests, setGuests] = useState<GuestSummary[]>([]);
  const [aggregate, setAggregate] = useState<AggregateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedGuest, setExpandedGuest] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'expired'>('all');
  const [sortBy, setSortBy] = useState<'engagement' | 'recent'>('engagement');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [guestsRes, aggRes] = await Promise.all([
          analyticsAPI.getGuests(),
          analyticsAPI.getAggregate(),
        ]);
        setGuests(guestsRes.data);
        setAggregate(aggRes.data);
      } catch (e) {
        console.error('Failed to fetch analytics:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleExpandGuest = async (userId: string) => {
    if (expandedGuest === userId) {
      setExpandedGuest(null);
      return;
    }
    setExpandedGuest(userId);
    setTimelineLoading(true);
    try {
      const res = await analyticsAPI.getGuestTimeline(userId);
      setTimeline(res.data);
    } catch (e) {
      console.error('Failed to fetch timeline:', e);
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  // Engagement rank for sorting
  const engagementRank: Record<string, number> = { high: 0, medium: 1, low: 2, none: 3 };

  const filteredGuests = useMemo(() => {
    let list = [...guests];
    if (filter === 'active') list = list.filter((g) => g.is_active);
    if (filter === 'expired') list = list.filter((g) => !g.is_active);
    if (sortBy === 'engagement') {
      list.sort((a, b) => (engagementRank[a.engagement] ?? 9) - (engagementRank[b.engagement] ?? 9));
    } else {
      list.sort((a, b) => {
        const aDate = a.last_seen ? new Date(a.last_seen).getTime() : 0;
        const bDate = b.last_seen ? new Date(b.last_seen).getTime() : 0;
        return bDate - aDate;
      });
    }
    return list;
  }, [guests, filter, sortBy]);

  // Feature bar chart data
  const featureBarData = useMemo(() => {
    if (!aggregate) return [];
    return Object.entries(aggregate.feature_usage)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [aggregate]);

  const maxFeatureCount = useMemo(() => Math.max(1, ...featureBarData.map((d) => d.count)), [featureBarData]);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="admin-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Summary Cards */}
      {(() => {
        const members = guests.filter(g => g.role !== 'guest' && g.role !== 'trial_member' && g.role !== 'trial' && g.is_active);
        const trialMembers = guests.filter(g => (g.role === 'trial_member' || g.role === 'trial') && g.is_active);
        const activeGuests = guests.filter(g => g.role === 'guest' && g.is_active);
        return (
          <div className="analytics-cards">
            <div className="analytics-card">
              <div className="analytics-card__label">Active Members</div>
              <div className="analytics-card__value" style={{ color: '#63b3ed' }}>
                {members.length}
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card__label">Active Trial Members</div>
              <div className="analytics-card__value" style={{ color: '#f6ad55' }}>
                {trialMembers.length}
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card__label">Active Guests</div>
              <div className="analytics-card__value" style={{ color: '#a371f7' }}>
                {activeGuests.length}
              </div>
            </div>
            <div className="analytics-card analytics-card--alert">
              <div className="analytics-card__label">Unopened</div>
              <div className="analytics-card__value" style={{ color: '#fc8181' }}>
                {aggregate?.unopened_guests ?? 0}
              </div>
              <div className="analytics-card__hint">action needed</div>
            </div>
          </div>
        );
      })()}

      {/* Second Row: Feature Usage + Path Distribution */}
      <div className="analytics-panels">
        {/* Feature Usage Bar Chart */}
        <div className="analytics-panel">
          <h3 className="analytics-panel__title">Most Used Features</h3>
          <div className="analytics-bars">
            {featureBarData.map(({ name, count }) => (
              <div key={name} className="analytics-bar-row">
                <span className="analytics-bar-label">{name}</span>
                <div className="analytics-bar-track">
                  <div
                    className="analytics-bar-fill"
                    style={{ width: `${(count / maxFeatureCount) * 100}%` }}
                  />
                </div>
                <span className="analytics-bar-count">{count}</span>
              </div>
            ))}
            {featureBarData.every((d) => d.count === 0) && (
              <div className="analytics-empty-hint">No activity yet</div>
            )}
          </div>
        </div>

        {/* Path Distribution */}
        <div className="analytics-panel">
          <h3 className="analytics-panel__title">Path Chosen at Entry</h3>
          <div className="analytics-paths">
            {Object.entries(PATH_CONFIG).map(([key, cfg]) => {
              const count = aggregate?.path_distribution?.[key] ?? 0;
              return (
                <div key={key} className="analytics-path-item">
                  <div className="analytics-path-icon" style={{ background: cfg.color }}>
                    {cfg.icon}
                  </div>
                  <div className="analytics-path-info">
                    <div className="analytics-path-name">{cfg.label}</div>
                    <div className="analytics-path-count" style={{ color: cfg.color }}>
                      {count} <span className="analytics-path-unit">{count === 1 ? 'person' : 'people'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Guest List */}
      <div className="analytics-section">
        <div className="analytics-section__header">
          <h3 className="analytics-section__title">Activity</h3>
          <div className="analytics-controls">
            <select
              className="analytics-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
            >
              <option value="all">All People</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
            <select
              className="analytics-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
            >
              <option value="engagement">Sort: Engagement</option>
              <option value="recent">Sort: Most Recent</option>
            </select>
          </div>
        </div>

        {filteredGuests.length === 0 ? (
          <div className="admin-table-container">
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
                <path d="M24 14V24L30 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <h3>No activity data yet</h3>
              <p>Activity will appear here once people start using the platform.</p>
            </div>
          </div>
        ) : (
          <div className="analytics-guest-list">
            {filteredGuests.map((guest) => {
              const isExpanded = expandedGuest === guest.user_id;
              const eng = ENGAGEMENT_CONFIG[guest.engagement];
              const path = guest.path_chosen ? PATH_CONFIG[guest.path_chosen] : null;

              return (
                <div key={guest.user_id} className={`analytics-guest ${isExpanded ? 'analytics-guest--expanded' : ''}`}>
                  <div className="analytics-guest__row" onClick={() => handleExpandGuest(guest.user_id)}>
                    {/* Status dot */}
                    <div
                      className="analytics-guest__status-dot"
                      style={{ background: guest.is_active ? '#68d391' : '#718096' }}
                      title={guest.is_active ? 'Active' : 'Expired'}
                    />

                    {/* Name + Company */}
                    <div className="analytics-guest__identity">
                      <div className="analytics-guest__name">{guest.guest_name}</div>
                      <div className="analytics-guest__company">{guest.company} &middot; {getRoleLabel(guest.role)}</div>
                    </div>

                    {/* Path badge */}
                    {path && (
                      <span className="analytics-guest__path-badge" style={{ color: path.color, background: `${path.color}20` }}>
                        {path.label}
                      </span>
                    )}

                    {/* Session dots */}
                    <div className="analytics-guest__sessions" title={`${guest.total_sessions} sessions`}>
                      {Array.from({ length: Math.min(guest.total_sessions, 10) }).map((_, i) => (
                        <div key={i} className="analytics-guest__session-dot" />
                      ))}
                      {guest.total_sessions === 0 && <span className="analytics-guest__no-sessions">--</span>}
                    </div>

                    {/* Engagement badge */}
                    <span
                      className="analytics-guest__engagement"
                      style={{ color: eng.color, background: eng.bg }}
                    >
                      {eng.label}
                    </span>

                    {/* Last seen */}
                    <div className="analytics-guest__last-seen">
                      {formatRelativeDate(guest.last_seen)}
                    </div>

                    {/* Expand arrow */}
                    <svg className={`analytics-guest__chevron ${isExpanded ? 'analytics-guest__chevron--open' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="analytics-guest__detail">
                      {timelineLoading ? (
                        <div className="loading-state" style={{ padding: '24px' }}>
                          <div className="admin-loading-spinner" />
                        </div>
                      ) : (
                        <>
                          {/* Stats row */}
                          <div className="analytics-detail__stats">
                            <div className="analytics-detail__stat">
                              <div className="analytics-detail__stat-value">{guest.total_sessions}</div>
                              <div className="analytics-detail__stat-label">Sessions</div>
                            </div>
                            <div className="analytics-detail__stat">
                              <div className="analytics-detail__stat-value">{guest.avg_session_minutes}</div>
                              <div className="analytics-detail__stat-label">Avg min</div>
                            </div>
                            <div className="analytics-detail__stat">
                              <div className="analytics-detail__stat-value">{guest.total_actions}</div>
                              <div className="analytics-detail__stat-label">Actions</div>
                            </div>
                            <div className="analytics-detail__stat">
                              <div className="analytics-detail__stat-value">{guest.exports}</div>
                              <div className="analytics-detail__stat-label">Exports</div>
                            </div>
                          </div>

                          {/* Feature usage bars */}
                          <div className="analytics-detail__section">
                            <h4 className="analytics-detail__heading">Feature Usage</h4>
                            <div className="analytics-bars analytics-bars--compact">
                              {Object.entries(guest.feature_usage).map(([feature, count]) => {
                                const maxVal = Math.max(1, ...Object.values(guest.feature_usage));
                                return (
                                  <div key={feature} className="analytics-bar-row">
                                    <span className="analytics-bar-label">{feature}</span>
                                    <div className="analytics-bar-track">
                                      <div className="analytics-bar-fill analytics-bar-fill--cyan" style={{ width: `${(count / maxVal) * 100}%` }} />
                                    </div>
                                    <span className="analytics-bar-count">{count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Search type breakdown */}
                          <div className="analytics-detail__section">
                            <h4 className="analytics-detail__heading">Search Types</h4>
                            <div className="analytics-detail__search-types">
                              {Object.entries(guest.search_types).map(([type, count]) => (
                                <div key={type} className="analytics-detail__search-type">
                                  <span className="analytics-detail__search-type-name">{type}</span>
                                  <span className="analytics-detail__search-type-count">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Recent activity timeline */}
                          {timeline.length > 0 && (
                            <div className="analytics-detail__section">
                              <h4 className="analytics-detail__heading">Recent Activity</h4>
                              <div className="analytics-timeline">
                                {timeline.slice(0, 20).map((event) => (
                                  <div key={event.id} className="analytics-timeline__event">
                                    <div className="analytics-timeline__dot" />
                                    <div className="analytics-timeline__content">
                                      <span className="analytics-timeline__action">{event.action_type.replace(/_/g, ' ')}</span>
                                      {event.page_area && (
                                        <span className="analytics-timeline__area">{event.page_area}</span>
                                      )}
                                    </div>
                                    <span className="analytics-timeline__time">
                                      {formatRelativeDate(event.created_at)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Follow-up advice */}
                          <div className="analytics-detail__followup">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M8 5V9M8 11V11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            {getFollowupAdvice(guest.engagement, guest.role)}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Privacy Footer */}
      <div className="analytics-privacy">
        This dashboard tracks feature usage and navigation patterns. It does not log specific compounds, targets, structures, or search queries. Members' and guests' research remains their own.
      </div>
    </div>
  );
};

export default AnalyticsPage;
